import { queryClient } from "@/config/react-query";

import {
  formatDate,
  formatList,
  formatStaffName,
  formatTime,
} from "@/lib/format-data";
import {
  fillSmsTemplate,
  isVideoVisit,
  startZoneSuffix,
} from "@/lib/sms-templates";
import { toMs } from "@/lib/timestamp";
import { getTimezone } from "@/lib/timezone";
import {
  zonedDateKey,
  zonedDayMarker,
  zonedWallMinutes,
} from "@/lib/timezone-dates";

import { UserType } from "@/types/enums";
import {
  EventCreate,
  EventDetail,
  EventSegmentTiny,
  EventServiceDetail,
  EventTiny,
  getServiceSegments,
} from "@/types/event";
import { FirebaseProviderSummary } from "@/types/provider";
import {
  EffectType,
  type SequenceDetail,
  type SmsEffectConfig,
  TriggerEvent,
} from "@/types/sequence";
import { ServiceDetail, ServiceTable } from "@/types/service";

import { MIN_EVENT_DURATION_MS } from "./constants";
import { dayWindows } from "./utils/calendar-clocks";
import { wallSpanMinutes } from "./utils/wall-time";
import { providerQueryKeys } from "@/api/provider/admin/provider-query";
import { BottomOverflowInfo, TopOverflowInfo } from "@/stores/calendar-store";

/** "Monday, June 9", for patient messages. */
const WEEKDAY_MONTH_DAY = {
  weekday: "long",
  month: "long",
  day: "numeric",
} as const;

/**
 * Returns effective start/end timestamps for layout purposes, enforcing
 * a minimum visual duration of MIN_EVENT_DURATION_MINUTES.
 *
 * When the item is short/zero-duration, extends forward unless that would
 * exceed `containerEndMs`, in which case it extends backward.
 */
export function getEffectiveLayoutBounds(
  startMs: number,
  endMs: number,
  containerEndMs: number,
): { startMs: number; endMs: number } {
  if (endMs - startMs >= MIN_EVENT_DURATION_MS) {
    return { startMs, endMs };
  }
  const forwardEnd = startMs + MIN_EVENT_DURATION_MS;
  if (forwardEnd <= containerEndMs) {
    return { startMs, endMs: forwardEnd };
  }
  return { startMs: Math.max(0, endMs - MIN_EVENT_DURATION_MS), endMs };
}

/** Layout bounds as the ISO `start`/`end` strings a virtual event carries. */
export function layoutBoundsToIso(bounds: { startMs: number; endMs: number }): {
  start: string;
  end: string;
} {
  return {
    start: new Date(bounds.startMs).toISOString(),
    end: new Date(bounds.endMs).toISOString(),
  };
}

/**
 * Gets a valid duration in minutes from an event, falling back to the existing
 * recurring event duration if the calculated value is invalid (0 or negative).
 */
export const getValidDurationMinutes = (event: EventCreate): number => {
  const calculatedDurationMinutes = Math.round(
    (event.end.getTime() - event.start.getTime()) / 60000,
  );

  if (calculatedDurationMinutes > 0) {
    return calculatedDurationMinutes;
  }

  const existingDuration = event.recurringEvent?.durationMinutes;
  if (existingDuration && existingDuration > 0) {
    return existingDuration;
  }

  return calculatedDurationMinutes;
};

export function getAppointmentEditHref(eventId: string): string {
  return `/check-in/appointment-form/${eventId}`;
}

export function getAppointmentRebookHref(event: EventTiny): string {
  const serviceSegments = getServiceSegments(event.segments || []);
  const patientId = event.attendees?.find(
    (attendee) => attendee.user.type === UserType.PATIENT,
  )?.user.id;
  const providerId =
    serviceSegments[0]?.providerId ??
    event.attendees?.find(
      (attendee) => attendee.user.type === UserType.PROVIDER,
    )?.user.id;
  const primaryServiceIds = serviceSegments
    .filter((segment) => !segment.serviceAddonId)
    .map((segment) => segment.service?.id)
    .filter(Boolean)
    .join(",");
  const serviceAddonIds = serviceSegments
    .map((segment) => segment.serviceAddonId)
    .filter(Boolean)
    .join(",");
  const params = new URLSearchParams();

  if (patientId) params.set("patientId", patientId);
  if (providerId) params.set("providerId", providerId);
  if (event.location?.id) params.set("locationId", event.location.id);
  if (primaryServiceIds) params.set("serviceIds", primaryServiceIds);
  if (serviceAddonIds) params.set("serviceAddonIds", serviceAddonIds);

  return `/check-in/appointment-form/new?${params.toString()}`;
}

export interface Position {
  top: number;
  bottom: number;
  left: number;
  width: number;
  bottomOverflow: boolean;
  topOverflow: boolean;
}

export interface EventWithPosition {
  event: EventTiny;
  position: Position;
  /** When set, render only this segment (day view per-provider split) */
  displaySegment?: EventSegmentTiny;
}

export interface ColumnLayout {
  left: number; // percentage 0-100
  width: number; // percentage 0-100
}

/**
 * Generic column-based layout for any items with start/end times.
 * Groups overlapping items into columns and expands them to fill available space.
 *
 * Algorithm based on: https://stackoverflow.com/a (Gabe Sidler, CC BY-SA 3.0)
 */
export function calculateColumnLayout<T extends { start: Date; end: Date }>(
  items: T[],
): ColumnLayout[] {
  const defaultLayout: ColumnLayout = { left: 0, width: 100 };
  if (items.length <= 1) return items.map(() => ({ ...defaultLayout }));

  interface ItemCalc {
    index: number;
    startMs: number;
    endMs: number;
  }

  const maxEndMs = Math.max(...items.map((item) => item.end.getTime()));

  const calcs: ItemCalc[] = items.map((item, index) => {
    const bounds = getEffectiveLayoutBounds(
      item.start.getTime(),
      item.end.getTime(),
      maxEndMs,
    );
    return { index, startMs: bounds.startMs, endMs: bounds.endMs };
  });

  calcs.sort((a, b) => {
    if (a.startMs !== b.startMs) return a.startMs - b.startMs;
    // Longer items first when start times are equal
    return b.endMs - b.startMs - (a.endMs - a.startMs);
  });

  const collides = (a: ItemCalc, b: ItemCalc) =>
    a.startMs < b.endMs && a.endMs > b.startMs;

  const results: ColumnLayout[] = items.map(() => ({ ...defaultLayout }));

  const packGroup = (columns: ItemCalc[][]) => {
    const n = columns.length;
    for (let c = 0; c < n; c++) {
      for (const item of columns[c]) {
        let colSpan = 1;
        for (let nc = c + 1; nc < n; nc++) {
          if (columns[nc].some((other) => collides(item, other))) break;
          colSpan++;
        }
        results[item.index] = {
          left: (c / n) * 100,
          width: (colSpan / n) * 100,
        };
      }
    }
  };

  const columns: ItemCalc[][] = [];
  let lastEnding: number | null = null;

  for (const calc of calcs) {
    if (lastEnding !== null && calc.startMs >= lastEnding) {
      if (columns.length > 0) packGroup(columns);
      columns.length = 0;
      lastEnding = null;
    }

    let placed = false;
    for (let c = 0; c < columns.length; c++) {
      const last = columns[c][columns[c].length - 1];
      if (!collides(last, calc)) {
        columns[c].push(calc);
        placed = true;
        break;
      }
    }
    if (!placed) columns.push([calc]);

    if (lastEnding === null || calc.endMs > lastEnding) {
      lastEnding = calc.endMs;
    }
  }

  if (columns.length > 0) packGroup(columns);

  return results;
}

export function calculateEventPositions(
  events: EventTiny[],
  startHour: number = 0,
  endHour: number = 24,
  day: Date | undefined,
  timeZone: string,
  zoneOfEvent?: (event: EventTiny) => string | undefined,
): {
  eventsWithPositions: EventWithPosition[];
  topOverflowInfo: TopOverflowInfo;
  bottomOverflowInfo: BottomOverflowInfo;
} {
  /**
   * Calculates the positions of events in the calendar view using a column-based
   * algorithm that groups overlapping events and expands them to fill available space.
   *
   * Algorithm based on: https://stackoverflow.com/a (Gabe Sidler, CC BY-SA 3.0)
   *
   * @param events - An array of Event objects to position for a single day
   * @param startHour - The starting hour of the visible calendar range (default: 0)
   * @param endHour - The ending hour of the visible calendar range (default: 24)
   * @param day - The column's day marker (defaults to first event's day)
   * @param timeZone - The display timezone; the day boundary, and so every
   *   event's vertical position, is anchored to it
   * @param zoneOfEvent - An event's own clock, when it has one: it then sits
   *   at its wall time there, from that clock's midnight on `day`
   * @returns An array of events with their calculated positions
   */

  if (events.length === 0) {
    return {
      eventsWithPositions: [],
      topOverflowInfo: { count: 0 },
      bottomOverflowInfo: { count: 0 },
    };
  }

  // The column's midnight in each event's zone; events sit at their
  // wall-clock rows from it, which on a DST day differ from elapsed time.
  const windowOf = dayWindows(day ?? zonedDayMarker(events[0].start, timeZone));
  const startMinute = startHour * 60;

  // Calculate the visible time range in milliseconds
  const MILLISECONDS_PER_SECOND = 1000;
  const SECONDS_PER_MINUTE = 60;
  const MINUTES_PER_HOUR = 60;
  const msPerDay =
    MILLISECONDS_PER_SECOND *
    SECONDS_PER_MINUTE *
    MINUTES_PER_HOUR *
    (endHour - startHour);

  const topOverflowInfo: TopOverflowInfo = { count: 0 };
  const bottomOverflowInfo: BottomOverflowInfo = { count: 0 };

  // Collision is checked on the *visual* [topTime, bottomTime] bounds (which
  // already include the DST wall-clock correction and the minimum layout
  // duration), not absolute instants — so on a fall-back fold the two
  // occurrences of the repeated hour, which share the same visual bounds, are
  // packed side by side instead of drawn on top of one another.
  const collidesWith = (a: EventCalculation, b: EventCalculation): boolean =>
    a.bottomTime > b.topTime && a.topTime < b.bottomTime;

  // Create an array of events with their calculated top/bottom positions
  interface EventCalculation {
    event: EventTiny;
    top: number; // Percentage from start of visible day
    bottom: number; // Percentage from end of visible day
    topTime: number; // Time in milliseconds for comparison
    bottomTime: number; // Time in milliseconds for comparison
  }

  const eventCalculations: EventCalculation[] = events.map((event) => {
    const zone = zoneOfEvent?.(event) ?? timeZone;
    const span = wallSpanMinutes(
      event.start,
      event.end,
      zone,
      windowOf(zone).start,
    );
    const topTime = (span.start - startMinute) * 60_000;
    // Never shorter than one slot.
    const bottomTime = Math.max(
      (span.end - startMinute) * 60_000,
      topTime + MIN_EVENT_DURATION_MS,
    );
    const top = (topTime / msPerDay) * 100;
    const bottom = ((msPerDay - bottomTime) / msPerDay) * 100;
    return {
      event,
      top,
      bottom,
      topTime,
      bottomTime,
    };
  });

  // Sort events by starting time, then by duration (descending) to prioritize longer events
  // This ensures longer events are placed on the left when they start at the same time
  eventCalculations.sort((e1, e2) => {
    if (e1.topTime < e2.topTime) return -1;
    if (e1.topTime > e2.topTime) return 1;
    // When start times are equal, sort by duration descending (longest first)
    const duration1 = e1.bottomTime - e1.topTime;
    const duration2 = e2.bottomTime - e2.topTime;
    if (duration1 > duration2) return -1;
    if (duration1 < duration2) return 1;
    return 0;
  });

  // Count how many events directly overlap with a given event (including itself).
  // Used to cap expansion so events sharing a time slot get balanced widths.
  const countDirectOverlaps = (ev: EventCalculation): number => {
    let count = 1; // include self
    for (const other of eventCalculations) {
      if (other !== ev && collidesWith(ev, other)) {
        count++;
      }
    }
    return count;
  };

  // Expand event to fill available space
  // Checks how many columns the event can expand into without colliding,
  // capped so the event doesn't exceed 1/directOverlaps of the group width.
  const expandEvent = (
    ev: EventCalculation,
    iColumn: number,
    columns: EventCalculation[][],
  ): number => {
    const maxColSpan = Math.max(
      1,
      Math.floor(columns.length / countDirectOverlaps(ev)),
    );
    let colSpan = 1;

    for (let i = iColumn + 1; i < columns.length; i++) {
      if (colSpan >= maxColSpan) return colSpan;
      const col = columns[i];
      for (let j = 0; j < col.length; j++) {
        const ev1 = col[j];
        if (collidesWith(ev, ev1)) {
          return colSpan;
        }
      }
      colSpan++;
    }

    return colSpan;
  };

  // Pack events in a group of columns
  const packEvents = (
    columns: EventCalculation[][],
    eventsWithPositions: EventWithPosition[],
  ): void => {
    const n = columns.length;

    for (let i = 0; i < n; i++) {
      const col = columns[i];
      for (let j = 0; j < col.length; j++) {
        const eventWithPos = col[j];
        const colSpan = expandEvent(eventWithPos, i, columns);

        const bottomOverflow = eventWithPos.bottom < 0;
        const topOverflow = eventWithPos.top < 0;
        const top = Math.max(0, eventWithPos.top);
        const bottom = Math.max(0, eventWithPos.bottom);

        if (top >= 100) {
          bottomOverflowInfo.count++;
          bottomOverflowInfo.latestEndMinute = Math.max(
            bottomOverflowInfo.latestEndMinute ?? -Infinity,
            eventWithPos.bottomTime / 60_000 + startMinute,
          );
          continue;
        }
        if (bottom >= 100) {
          topOverflowInfo.count++;
          topOverflowInfo.earliestStartMinute = Math.min(
            topOverflowInfo.earliestStartMinute ?? Infinity,
            eventWithPos.topTime / 60_000 + startMinute,
          );
          continue;
        }
        eventsWithPositions.push({
          event: eventWithPos.event,
          position: {
            top,
            bottom,
            left: (i / n) * 100,
            width: (colSpan / n) * 100,
            bottomOverflow,
            topOverflow,
          },
        });
      }
    }
  };

  // Main algorithm: group events and assign columns
  const columns: EventCalculation[][] = [];
  let lastEventEnding: number | null = null;
  const eventsWithPositions: EventWithPosition[] = [];

  for (const eventWithPos of eventCalculations) {
    // Check if a new event group needs to be started
    if (lastEventEnding !== null && eventWithPos.topTime >= lastEventEnding) {
      // The latest event is later than any of the events in the current group.
      // There is no overlap. Output the current event group and start a new event group.
      if (columns.length > 0) {
        packEvents(columns, eventsWithPositions);
      }
      columns.length = 0; // This starts new event group.
      lastEventEnding = null;
    }

    // Try to place the event inside the existing columns
    // Collect all valid columns, preferring leftmost columns for longer events
    const validColumns: number[] = [];
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const lastEventInColumn = col[col.length - 1];
      if (!collidesWith(lastEventInColumn, eventWithPos)) {
        validColumns.push(i);
      }
    }

    let placed = false;
    if (validColumns.length > 0) {
      // If multiple columns are available, prefer leftmost (already sorted)
      // Place the event in the first valid column
      const targetColumn = validColumns[0];
      columns[targetColumn].push(eventWithPos);
      placed = true;
    }

    // It was not possible to place the event. Add a new column for the current event group.
    if (!placed) {
      columns.push([eventWithPos]);
    }

    // Remember the latest event end time of the current group.
    // This is later used to determine if a new group starts.
    if (lastEventEnding === null || eventWithPos.bottomTime > lastEventEnding) {
      lastEventEnding = eventWithPos.bottomTime;
    }
  }

  // Process remaining events in the last group
  if (columns.length > 0) {
    packEvents(columns, eventsWithPositions);
  }

  return {
    eventsWithPositions,
    topOverflowInfo,
    bottomOverflowInfo,
  };
}

export function getNumberOfOverlappingEventsByISOTime(
  start: Date,
  end: Date,
  events: EventTiny[],
): number {
  return events.filter((event) => {
    const eventStart = toMs(event.start);
    const eventEnd = toMs(event.end);
    const rangeStart = start.getTime();
    const rangeEnd = end.getTime();

    return (
      (eventStart < rangeEnd && eventEnd > rangeStart) ||
      (eventStart === rangeStart && eventEnd === rangeEnd)
    );
  }).length;
}

export interface GroupedServices {
  atSelectedLocation?: ServiceDetail[];
  atOtherLocations?: ServiceDetail[];
}

export interface SelectedServiceItem {
  serviceId: string;
}

/**
 * The booking confirmation SMS template for a sequence: the delay-0
 * APPOINTMENT_BOOKED / SEND_SMS step. Mirrors the backend, which fires exactly
 * that step on booking (`_fire_instant_sequence_steps(..., APPOINTMENT_BOOKED)`).
 *
 * Deliberately NOT filtered on `isActive`: an explicit booking passes
 * `include_inactive=True`, so a deactivated step still sends and must still
 * preview.
 */
export function getBookedSmsStepTemplate(
  sequence: SequenceDetail | null | undefined,
): string | undefined {
  const step = sequence?.steps?.find(
    (s) =>
      s.triggerEvent === TriggerEvent.APPOINTMENT_BOOKED &&
      s.effectType === EffectType.SEND_SMS &&
      s.delayMinutes === 0,
  );
  const template = (step?.config?.effect as SmsEffectConfig | undefined)
    ?.template;
  return template || undefined;
}

/**
 * The reminder SMS template for a sequence, for the staff-initiated "Send
 * Reminder Text" action. Reminders are APPOINTMENT_STARTING / SEND_SMS steps
 * with a negative delay (minutes BEFORE the appointment).
 *
 * A service may have several — e.g. 24h and 1h before. We take the one closest
 * to the appointment (smallest |delay|): its wording is the final-nudge phrasing
 * that suits an ad-hoc manual send, whereas the earliest step is usually written
 * as a first heads-up.
 *
 * Deliberately NOT filtered on `isActive`: this is an explicit staff action, so
 * a service whose automatic reminders are switched off should still offer its
 * authored wording rather than falling back to nothing.
 *
 * Conditioned steps are excluded. APPOINTMENT_STARTING also carries the override
 * notice (`EVENT_NOT_CONFIRMED`, −120 in the default sequence) — a rebooking
 * warning, not a reminder. Because it sits closer to the appointment than the
 * −1440 reminder, picking by smallest |delay| alone would draft the warning for
 * every service on the default sequence. An unconditional STARTING SMS is the
 * reminder; a conditioned one is a notice.
 */
export function getReminderSmsStepTemplate(
  sequence: SequenceDetail | null | undefined,
): string | undefined {
  const reminderSteps = (sequence?.steps ?? []).filter(
    (s) =>
      s.triggerEvent === TriggerEvent.APPOINTMENT_STARTING &&
      s.effectType === EffectType.SEND_SMS &&
      s.delayMinutes < 0 &&
      !s.conditionType,
  );
  if (reminderSteps.length === 0) return undefined;

  const closest = reminderSteps.reduce((best, step) =>
    Math.abs(step.delayMinutes) < Math.abs(best.delayMinutes) ? step : best,
  );
  const template = (closest.config?.effect as SmsEffectConfig | undefined)
    ?.template;
  return template || undefined;
}

/**
 * Confirmation template to preview for the current selection. Uses the first
 * selected service's sequence — a multi-service appointment sends one
 * confirmation, and the backend composes it from the first service's sequence
 * too.
 *
 * Replaces the deleted `getConfirmationTemplate`, which read the retired
 * `service.confirmationSmsTemplate` column.
 */
export function getBookedConfirmationTemplate(
  services: ServiceTable[] | undefined,
  selectedServices: SelectedServiceItem[],
): string | undefined {
  if (!services || selectedServices.length === 0) return undefined;

  const firstServiceId = selectedServices[0]?.serviceId;
  if (!firstServiceId) return undefined;

  const firstService = services.find((s) => s.id === firstServiceId);

  return getBookedSmsStepTemplate(firstService?.sequence);
}

export function generateAppointmentUpdateMessage(
  // Accept EventTiny so the calendar drag-reschedule path (which works off
  // EventTiny + a drop target) can reuse this. EventDetail extends EventTiny, so
  // the appointment form's existing EventDetail callers stay compatible.
  originalEvent: EventTiny,
  updatedEvent: EventTiny,
  tenantName: string,
  // The clock the patient reads the times on: the appointment's location.
  timeZone: string = getTimezone(),
  // The original location's clock, when the move changes location. A move
  // that keeps the wall time between two zones is no time change.
  originalTimeZone: string = timeZone,
): string {
  const changes: string[] = [];
  // A time line that moves the appointment to another day names both dates;
  // otherwise the opening sentence names the day, so a same-day change still
  // says which appointment it is about.
  let timeLineHasDates = false;

  // Read from the location and services, not `isVirtual`, which an edited copy
  // of the original event still carries over.
  const byVideo = (event: EventTiny) =>
    isVideoVisit(
      event.location,
      getServiceSegments(event.segments || [])
        .filter((segment) => !segment.serviceAddonId)
        .map((segment) => segment.service ?? {}),
    );

  const wallTime = (instant: string, zone: string) =>
    `${zonedDateKey(instant, zone)} ${zonedWallMinutes(instant, zone)}`;
  const timeChanged =
    originalTimeZone === timeZone
      ? toMs(originalEvent.start) !== toMs(updatedEvent.start) ||
        toMs(originalEvent.end) !== toMs(updatedEvent.end)
      : wallTime(originalEvent.start, originalTimeZone) !==
          wallTime(updatedEvent.start, timeZone) ||
        wallTime(originalEvent.end, originalTimeZone) !==
          wallTime(updatedEvent.end, timeZone);

  // Check time changes
  if (timeChanged) {
    // Compare calendar days on the clocks the message formats in, so a
    // same-day move doesn't falsely claim a date change (and a real one isn't
    // dropped).
    const dateChanged =
      zonedDateKey(originalEvent.start, originalTimeZone) !==
      zonedDateKey(updatedEvent.start, timeZone);
    timeLineHasDates = dateChanged;

    const oldStart = dateChanged
      ? formatDate(originalEvent.start, {
          ...WEEKDAY_MONTH_DAY,
          timeZone: originalTimeZone,
        }) +
        " at " +
        formatTime(originalEvent.start, true, originalTimeZone)
      : formatTime(originalEvent.start, true, originalTimeZone);

    // A video visit's patient may be anywhere, so its times name the clinic's
    // zone, as the server's `{start}` does.
    const oldEnd =
      formatTime(originalEvent.end, true, originalTimeZone) +
      startZoneSuffix(
        new Date(originalEvent.end),
        originalTimeZone,
        byVideo(originalEvent),
      );

    const newStart = dateChanged
      ? formatDate(updatedEvent.start, { ...WEEKDAY_MONTH_DAY, timeZone }) +
        " at " +
        formatTime(updatedEvent.start, true, timeZone)
      : formatTime(updatedEvent.start, true, timeZone);

    const newEnd =
      formatTime(updatedEvent.end, true, timeZone) +
      startZoneSuffix(
        new Date(updatedEvent.end),
        timeZone,
        byVideo(updatedEvent),
      );

    changes.push(
      `time from ${oldStart} - ${oldEnd} to ${newStart} - ${newEnd}`,
    );
  }

  // Check location changes
  if (originalEvent.location?.id !== updatedEvent.location?.id) {
    const oldLocation = originalEvent.location?.name || "TBD";
    const newLocation = updatedEvent.location?.name || "TBD";
    changes.push(`location from ${oldLocation} to ${newLocation}`);
  }

  // Check service changes
  const originalServiceTitles = getServiceSegments(originalEvent.segments || [])
    .map((es) => es.service?.name)
    .filter(Boolean)
    .sort();
  const updatedServiceTitles = getServiceSegments(updatedEvent.segments || [])
    .map((es) => es.service?.name)
    .filter(Boolean)
    .sort();

  const servicesChanged =
    originalServiceTitles.length !== updatedServiceTitles.length ||
    originalServiceTitles.some(
      (title, index) => title !== updatedServiceTitles[index],
    );

  if (servicesChanged) {
    // Find added and removed services
    const addedServices = updatedServiceTitles.filter(
      (title) => !originalServiceTitles.includes(title),
    );
    const removedServices = originalServiceTitles.filter(
      (title) => !updatedServiceTitles.includes(title),
    );

    if (addedServices.length > 0 && removedServices.length > 0) {
      // Both added and removed
      const added = formatList(addedServices);
      const removed = formatList(removedServices);
      changes.push(`added ${added}`, `removed ${removed}`);
    } else if (addedServices.length > 0) {
      // Only added
      const added = formatList(addedServices);
      changes.push(`added ${added}`);
    } else if (removedServices.length > 0) {
      // Only removed
      const removed = formatList(removedServices);
      changes.push(`removed ${removed}`);
    }
  }

  // Check provider changes
  const getEventProvider = (event: EventTiny) => {
    // Prefer provider from segments via providerId
    const serviceSegments = getServiceSegments(event.segments || []);
    const serviceWithProviderId = serviceSegments.find((es) => es.providerId);
    if (serviceWithProviderId?.providerId) {
      const provider = queryClient.getQueryData<FirebaseProviderSummary>(
        providerQueryKeys.summary(serviceWithProviderId.providerId),
      );
      if (provider) return provider;
    }
    // Fallback to attendees for provider
    const providerAttendee = event.attendees?.find(
      (attendee) => attendee.user.type === "PROVIDER",
    );
    return providerAttendee?.user;
  };

  const originalProvider = getEventProvider(originalEvent);
  const updatedProvider = getEventProvider(updatedEvent);

  if (originalProvider?.id !== updatedProvider?.id) {
    const oldProviderName = originalProvider
      ? `${formatStaffName(originalProvider)}`
      : "TBD";
    const newProviderName = updatedProvider
      ? `${formatStaffName(updatedProvider)}`
      : "TBD";
    changes.push(`provider from ${oldProviderName} to ${newProviderName}`);
  }

  // A change of services can move the visit between in person and video with
  // the location unchanged, and a video visit can move to a clinic and stay by
  // video. `{location}` is left for the server, which words it for the visit's
  // delivery: the Meet link for video, the address in person. Without it, a
  // video visit's new clinic reads as a place to go.
  const deliveryChanged = byVideo(originalEvent) !== byVideo(updatedEvent);
  const movedByVideo =
    byVideo(updatedEvent) &&
    originalEvent.location?.id !== updatedEvent.location?.id;

  const appointmentDay = timeLineHasDates
    ? ""
    : ` on ${formatDate(updatedEvent.start, { ...WEEKDAY_MONTH_DAY, timeZone })}`;
  let messageStr = `Your appointment with ${tenantName}${appointmentDay} has been updated. `;

  if (changes.length === 1) {
    const change = changes[0];
    // Check if change already starts with an action word
    if (change.startsWith("added ") || change.startsWith("removed ")) {
      messageStr += `${change.charAt(0).toUpperCase() + change.slice(1)}.`;
    } else {
      messageStr += `Changed ${change}.`;
    }
  } else if (changes.length > 1) {
    // Format multiple changes properly
    const formattedChanges = changes.map((change, index) => {
      if (index === 0) {
        // Capitalize first change
        if (change.startsWith("added ") || change.startsWith("removed ")) {
          return change.charAt(0).toUpperCase() + change.slice(1);
        } else {
          return `Changed ${change}`;
        }
      } else {
        // Subsequent changes
        if (change.startsWith("added ") || change.startsWith("removed ")) {
          return change;
        } else {
          return `changed ${change}`;
        }
      }
    });
    messageStr += formatList(formattedChanges, undefined, ", and ");
  } else {
    messageStr = `Your appointment with ${tenantName}${appointmentDay} has been updated.`;
  }

  if (!deliveryChanged && !movedByVideo) return messageStr;
  // A list of changes has no closing full stop of its own.
  return `${/[.!?]$/.test(messageStr) ? messageStr : `${messageStr}.`} {location}`;
}

/**
 * The service segment whose sequence fires for the appointment: the
 * earliest-created one, tie-broken on id — the backend's `Event.primary_service`
 * rule. Falls back to list order when the payload carries no creation dates.
 */
function getPrimaryServiceSegment(
  event: EventDetail,
): EventServiceDetail | undefined {
  const segments = getServiceSegments(event.segments || []);
  if (segments.some((segment) => !segment.createdDate)) return segments[0];
  return [...segments].sort((a, b) => {
    const byDate =
      new Date(a.createdDate!).getTime() - new Date(b.createdDate!).getTime();
    if (byDate !== 0) return byDate;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  })[0];
}

/**
 * The cancellation SMS template the appointment sends: the primary service's
 * delay-0 APPOINTMENT_CANCELLED sequence step — the one `cancel_event` fires.
 * Returns undefined when that service has none.
 */
export function getEventCancellationTemplate(
  event: EventDetail,
): string | undefined {
  const template =
    getPrimaryServiceSegment(event)?.cancellationSmsStepTemplate?.trim();
  return template || undefined;
}

/**
 * Preview of the cancellation SMS for the staff-editable box.
 *
 * When the service's sequence has a cancellation step, that step's template is
 * what the patient gets, so it is what we show — filled the way the backend
 * hydrates it. Tokens we can't resolve client-side (`{forms}`, `{portal_url}`,
 * …) are left as `{token}`: an unedited message is sent as `undefined` so the
 * backend hydrates the template in full (see `AppointmentCancellationModal`),
 * and an edited one is hydrated by the backend too, so neither path can drop
 * the dynamic content those tokens stand for.
 *
 * Only when no template is configured do we compose the generic message, which
 * names the service and provider.
 */
export function generateAppointmentCancellationMessage(
  event: EventDetail,
  tenantName?: string,
  timeZone: string = getTimezone(),
): string {
  const serviceTitle = formatList(
    getServiceSegments(event.segments || []).map((svc) => svc.service?.name),
  );

  const getEventProvider = (event: EventDetail) => {
    // Prefer provider from segments via providerId
    const serviceSegments = getServiceSegments(event.segments || []);
    const serviceWithProviderId = serviceSegments.find((es) => es.providerId);
    if (serviceWithProviderId?.providerId) {
      const provider = queryClient.getQueryData<FirebaseProviderSummary>(
        providerQueryKeys.summary(serviceWithProviderId.providerId),
      );
      if (provider) return provider;
    }
    // Fallback to attendees for provider
    const providerAttendee = event.attendees?.find(
      (attendee) => attendee.user.type === "PROVIDER",
    );
    return providerAttendee?.user;
  };

  const provider = getEventProvider(event);
  const providerName = provider ? formatStaffName(provider) : "";

  const startTime =
    formatDate(event.start, { ...WEEKDAY_MONTH_DAY, timeZone }) +
    " at " +
    formatTime(event.start, true, timeZone);

  const template = getEventCancellationTemplate(event);
  if (template) {
    const patient = event.attendees?.find(
      (attendee) => attendee.user.type === UserType.PATIENT,
    )?.user;
    // Resolve ONLY the three values the backend's hydration resolves the same
    // way the console can, so we can mirror them exactly. Everything
    // else — {start}, {date}, {location}, {provider_name}, {service}, {title},
    // {forms}, … — stays a token for the backend to hydrate, because the
    // console cannot reproduce those values faithfully:
    //   • {start}/{date} are localised to the appointment location's timezone,
    //     not the staff browser's, and {date} uses a different format;
    //   • {provider_name}/{provider_first_name} aggregate and dedupe across all
    //     service segments, not just the first;
    //   • {location} carries the meeting link for virtual appointments;
    //   • {title} is derived from the services, not `event.title`.
    // Resolving any of them here would ship a wrong value the moment staff edit
    // the text, since an edited message is sent as-is (then hydrated).
    return fillSmsTemplate(
      template,
      {
        first_name: patient?.firstName,
        tenant_name: tenantName,
        location_name: event.location?.name || tenantName,
      },
      { keepUnresolved: true },
    );
  }

  if (providerName) {
    return `Your ${serviceTitle || "appointment"} with ${providerName} scheduled for ${startTime} has been cancelled. Please contact us if you have any questions.`;
  } else {
    return `Your ${serviceTitle || "appointment"} scheduled for ${startTime} has been cancelled. Please contact us if you have any questions.`;
  }
}
